import Heading from '@legacy-vfuk/core-heading';
import { NavigationalTabs, Tab } from '@legacy-vfuk/core-navigational-tabs';
import { Element, scroller } from 'react-scroll';

import {
  CONTENTFUL_SECTION_SW_ACCORDION_CARD_CAROUSEL,
  CONTENTFUL_SECTION_SW_CONTENT_BLOCK_WITH_VIDEO_GROUP,
  CONTENTFUL_SECTION_SW_FAQS,
  CONTENTFUL_SECTION_SW_OVERVIEW_FEATURE,
  CONTENTFUL_SECTION_SW_RELATED_ARTICLES,
  CONTENTFUL_SECTION_SW_RELATED_PRODUCTS,
  CONTENTFUL_SECTION_CARD_DISPLAY,
  CONTENTFUL_SECTION_SW_GENERIC_CAROUSEL,
  CONTENTFUL_SECTION_GROUPED_UTILITY_CARD,
  CONTENTFUL_SECTION_SW_TARIFF_CARDS,
} from '@/templates/page-builder-helpers';

import SWAccordionCardCarousel from '../SWAccordionCardCarousel';
import SWContentBlockWithVideo from '../SWContentBlockWithVideo';
import SWFeatureOverview from '../SWFeatureOverview/SWFeatureOverview';
import SWPricingContent, { CONTENTFUL_SECTION_SW_PRICING_CONTENT } from '../SWPricingContent';
import SWRelatedArticles from '../SWRelatedArticles';
import SWRelatedProducts from '../SWRelatedProducts';
import SWSectionFAQs from '../SWSectionFAQs';
import { CustomElement, NavigationalTabsWrapper } from './SWNavigationalTabs.styles';
import { RenderTabProps, SWNavigationalTabsProps, TabProps } from './SWNavigationalTabs.types';
import SWGenericCarousel from '../SWGenericCarousel/SWGenericCarousel';
import SectionCardDisplay from '../SectionCardDisplay/SectionCardDisplay';
import SectionGroupedUtilityCard from '../SectionGroupedUtilityCard/SectionGroupedUtilityCard';
import SWSectionTariffCards from '../SWSectionTariffCards';
import { useEffect, useState } from 'react';

const SWNavigationalTabs = ({ tabs, sticky, biggerType }: SWNavigationalTabsProps) => {
  const [isNavTabsSticky, setIsNavTabsSticky] = useState(false);
  const OPCO = process.env.GATSBY_OPCO;
  const handleOnTabClick = (tabId: string) => {
    scroller.scrollTo(tabId, {
      smooth: true,
      spy: false,
      offset: -113,
    });
  };

  useEffect(() => {
    const listener = (event: Event) => {
      const customEvent = event as CustomEvent<{ typename: string }>;
      if (customEvent.detail?.typename) {
        // Find the tab with matching typename
        const matchedTab = tabs.find(tab => tab.__typename === customEvent.detail.typename);
        if (matchedTab) {
          handleOnTabClick(matchedTab.id); // use tab.id to activate the tab
        }
      }
    };

    window.addEventListener('switch-tab', listener);
    return () => {
      window.removeEventListener('switch-tab', listener);
    };
  }, [tabs]);

  useEffect(() => {
    if (!sticky) return; // Only add scroll listener if tabs should be sticky

    const handleScroll = () => {
      // Try multiple selectors to find the nav tabs element
      const navTabsListElement =
        document.querySelector('[class*="NavigationalTabsstyle__TabList-sc"]') ||
        document.querySelector('[class*="TabList"]') ||
        document.querySelector('[role="tablist"]');

      if (navTabsListElement) {
        const rect = navTabsListElement.getBoundingClientRect();

        // Determine expected sticky position based on current state
        const hasPhbScroll = document.body.classList.contains('has-phb-scroll');
        let expectedStickyTop = 108; // Default sticky position

        if (hasPhbScroll) {
          // When ProductHeaderBannerScroll is visible
          expectedStickyTop = OPCO === 'portugal' ? 160 : 196;
        }

        // Check if element has position sticky and is near the top
        const computedStyle = window.getComputedStyle(navTabsListElement);
        const isPositionSticky = computedStyle.position === 'sticky';
        const isAtTop = rect.top <= expectedStickyTop + 20;

        const isSticky = isPositionSticky && isAtTop;
        setIsNavTabsSticky(isSticky);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sticky, OPCO]);

  useEffect(() => {
    const className = 'has-nav-tabs-sticky';
    if (isNavTabsSticky) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => document.body.classList.remove(className);
  }, [isNavTabsSticky]);

  const renderTabs = (tabs: TabProps[]) => {
    return tabs.map(({ ...tab }) => {
      switch (tab.__typename) {
        case CONTENTFUL_SECTION_SW_ACCORDION_CARD_CAROUSEL: {
          return (
            <Tab key={tab.id} id={tab.id} text={tab.tabName}>
              <RenderTab tab={tab}>
                <SWAccordionCardCarousel key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }

        case CONTENTFUL_SECTION_SW_CONTENT_BLOCK_WITH_VIDEO_GROUP: {
          return (
            <Tab key={tab.id} id={tab.id} text={tab.tabName}>
              <RenderTab tab={tab}>
                <SWContentBlockWithVideo key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }

        case CONTENTFUL_SECTION_SW_PRICING_CONTENT: {
          return (
            <Tab key={tab.id} id={`section-${tab.id}`} text={tab.tabName}>
              <RenderTab tab={tab}>
                <SWPricingContent key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }

        case CONTENTFUL_SECTION_SW_OVERVIEW_FEATURE: {
          return (
            <Tab key={tab.id} id={tab.id} text={tab.tabName}>
              <RenderTab tab={{ id: tab.id }}>
                <SWFeatureOverview key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }

        case CONTENTFUL_SECTION_SW_RELATED_ARTICLES: {
          return (
            <Tab key={tab.id} id={tab.id} text={tab.tabName}>
              <RenderTab tab={tab}>
                <SWRelatedArticles key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }

        case CONTENTFUL_SECTION_SW_RELATED_PRODUCTS: {
          return (
            <Tab key={tab.id} id={tab.id} text={tab.tabName}>
              <RenderTab tab={tab}>
                <SWRelatedProducts key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }

        case CONTENTFUL_SECTION_SW_FAQS: {
          return (
            <Tab key={tab.id} id={tab.id} text={tab.heading}>
              <RenderTab tab={tab}>
                <SWSectionFAQs key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }

        case CONTENTFUL_SECTION_SW_GENERIC_CAROUSEL: {
          return (
            <Tab key={tab.id} id={tab.id} text={tab.title}>
              <RenderTab tab={tab} showHeading={false}>
                <SWGenericCarousel key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }

        case CONTENTFUL_SECTION_CARD_DISPLAY: {
          return (
            <Tab key={tab.id} id={tab.id} text={tab.title}>
              <RenderTab tab={tab} showHeading={false}>
                <SectionCardDisplay key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }

        case CONTENTFUL_SECTION_GROUPED_UTILITY_CARD: {
          return (
            <Tab key={tab.id} id={tab.id} text={tab.tabName}>
              <RenderTab tab={tab} showHeading={false}>
                <SectionGroupedUtilityCard key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }
        case CONTENTFUL_SECTION_SW_TARIFF_CARDS: {
          return (
            <Tab key={tab.id} id={tab.id} text={tab.tabName}>
              <RenderTab tab={tab}>
                <SWSectionTariffCards key={tab.id} {...tab} />
              </RenderTab>
            </Tab>
          );
        }

        default: {
          return <></>;
        }
      }
    });
  };
  return (
    <NavigationalTabsWrapper sticky={sticky} tabsNumber={tabs.length} OPCO={OPCO} biggerType={biggerType}>
      <NavigationalTabs accessibilityInfiniteScroll={true} onTabClick={handleOnTabClick}>
        {renderTabs(tabs)}
      </NavigationalTabs>
    </NavigationalTabsWrapper>
  );
};

const RenderTab = ({ tab, children, showHeading = true }: RenderTabProps) => {
  const { backgroundColor } = tab;
  return (
    <Element name={tab.id}>
      {showHeading ? (
        <CustomElement backgroundColor={backgroundColor || 'primary2.default'}>
          {tab.__typename === 'ContentfulSectionSwfaQs' ? null : tab.tabName ? (
            <Heading level={2} justify={'center'} text={tab.tabName} />
          ) : tab.name ? (
            <Heading level={2} justify={'center'} text={tab.name} />
          ) : null}
          {children}
        </CustomElement>
      ) : (
        <>{children}</>
      )}
    </Element>
  );
};

export default SWNavigationalTabs;
